import { useEffect, useMemo, useRef, useState } from 'react';

import { useAnimationFrame } from '../../../../helpers/hooks';

interface Position {
  x: number;
  y: number;
}

interface PositionWithAngle extends Position {
  angle?: number;
}

const DOT_DISTANCE = 74;
const DOT_DISTANCE_SMALL = 20;

const constantFlowRate = (rate: number): number => {
  return Math.sign(rate);
};

const useFlowAnimation = (
  pathRef: React.RefObject<SVGPathElement>,
  rate: number,
  disabled: boolean,
  tl: number
) => {
  const totalLength = useRef<number>(0);
  const [dotPos, setDotPos] = useState<PositionWithAngle[]>([]);
  const lastTS = useRef<number>(0);
  const offset = useRef<number>(0);
  const constantRate = useMemo(() => constantFlowRate(rate), [rate]);
  const dotDistance = useMemo(() => {
    if (pathRef.current && pathRef.current.getTotalLength() < 20) {
      return DOT_DISTANCE_SMALL;
    }
    return DOT_DISTANCE;
  }, [pathRef]);

  // Reset the dot position when the timeline changes
  useEffect(() => {
    offset.current = 0;
    totalLength.current = 0;
    setDotPos([]);
  }, [tl]);

  // Animation frame handler
  useAnimationFrame(
    (ts) => {
      if (disabled || !pathRef.current) return;

      // Ensure we have a valid path length
      if (totalLength.current <= 0 && pathRef.current) {
        totalLength.current = pathRef.current.getTotalLength();
      }

      if (totalLength.current <= 0) return;

      // Calculate time delta and update offset
      const timeDelta = lastTS.current ? ts - lastTS.current : 0;

      // Use a constant speed in pixels per millisecond
      const adjustedRate = totalLength.current < 20 ? 0.006 : 0.01;
      const pixelsPerMs = Math.abs(constantRate) * adjustedRate;
      const pixelOffset = pixelsPerMs * timeDelta;

      const node = pathRef.current;
      const currentTotalLength = totalLength.current;

      // Update offset (keep it within path length)
      offset.current = (offset.current + pixelOffset * Math.sign(constantRate)) % dotDistance;
      if (offset.current < 0) offset.current += dotDistance;

      const dots: PositionWithAngle[] = [];
      const direction = Math.sign(constantRate);

      // Create fixed positions starting from offset
      let currentPosition = offset.current;
      while (currentPosition < currentTotalLength) {
        // Get point at position
        const point = node.getPointAtLength(currentPosition);
        if (!point) {
          currentPosition += dotDistance;
          continue;
        }

        // Calculate angle (direction of movement)
        const delta = 0.1;
        // For negative rate, look backwards for the angle calculation
        const nextPosition =
          direction >= 0
            ? Math.min(currentPosition + delta, currentTotalLength)
            : Math.max(currentPosition - delta, 0);

        const pointAhead = node.getPointAtLength(nextPosition);

        if (!pointAhead) {
          dots.push({ x: point.x, y: point.y });
          currentPosition += dotDistance;
          continue;
        }

        const angle = Math.atan2(pointAhead.y - point.y, pointAhead.x - point.x) * (180 / Math.PI);

        dots.push({
          x: point.x,
          y: point.y,
          angle: angle,
        });

        currentPosition += dotDistance;
      }

      // If moving backwards, reverse the array but keep the angles as is
      if (constantRate < 0) {
        dots.reverse();
      }

      setDotPos(dots);
      lastTS.current = ts;
    },
    [rate, disabled, pathRef.current, tl]
  );
  return dotPos;
};

export default useFlowAnimation;
